/**
 * ENG-8449 — how long a "Force Update" one-shot may sit un-applied before the
 * host stops asking whether it is polite to proceed.
 *
 * The failure this bounds: on `agt-aws-1` (14 agents) an operator clicked Force
 * Update and the manager deferred it 380 consecutive polls — 78 minutes —
 * before it happened to land. ENG-8079's escalation was already in force. It did
 * not help for two independent reasons:
 *
 *  1. Relaxing drops only the inbound-quiet axis. The surviving progress axis is
 *     OR-ed across EVERY agent on the host, and when transcript age can't be
 *     resolved it falls back to `pane.log` mtime — the raw tmux stream, which any
 *     pane redraw refreshes. Three agents measured 0s during the incident. A
 *     further axis drop would fail the same way.
 *  2. The escalation counter is a module-level `let` in the manager, so every
 *     manager restart rewinds it to 0. On a frequently-restarting host the
 *     threshold may never be reached at all.
 *
 * Both thresholds therefore hang off wall-clock time since the API-stamped
 * `hosts.update_requested_at`, which no restart can rewind.
 *
 * These live in core rather than in the manager because the console renders the
 * same deadline (the "update pending" chip reads overdue past the ceiling). Two
 * copies of the number would drift silently, and the drift is invisible: the chip
 * would simply describe a deadline the host is not keeping.
 *
 * Everything here is pure — the caller supplies `nowMs`.
 */

/**
 * When the idle gate relaxes to the progress axis alone (ENG-8079's tier,
 * re-anchored to the clock). ~7 minutes is what 20 consecutive defers costs on a
 * healthy host at 20-30s polls, so a stable manager behaves as it did before.
 */
export const FORCED_UPDATE_RELAX_AFTER_MS = 7 * 60_000;

/**
 * The hard ceiling: past this the idle gate is not consulted at all.
 *
 * The trade, deliberately: at the ceiling this will occasionally interrupt a live
 * turn on a busy host. That is the semantics of an operator explicitly clicking
 * Force Update — they have already decided the upgrade outweighs turn continuity
 * — and the restart-notice machinery tells the customer the agent restarted.
 * Below the ceiling ENG-6692's politeness is untouched.
 */
export const FORCED_UPDATE_HARD_DEADLINE_MS = 15 * 60_000;

/**
 * How long a forced request has been pending, measured from the API-stamped
 * `update_requested_at`. Null when there is no request or its timestamp is
 * unparseable — fail safe, so nothing ever escalates on a value that can't be
 * compared.
 *
 * Negative elapsed is clamped to 0: the anchor is the control plane's clock and
 * the reading is the caller's, so a host running behind would otherwise produce a
 * negative age and push its own deadline permanently out of reach.
 */
export function forcedUpdatePendingForMs(opts: {
  requestedAt: string | null | undefined;
  nowMs: number;
}): number | null {
  if (!opts.requestedAt) return null;
  const requestedMs = Date.parse(opts.requestedAt);
  if (Number.isNaN(requestedMs)) return null;
  return Math.max(0, opts.nowMs - requestedMs);
}

/**
 * Has a pending forced update passed the hard ceiling? A null age (no request,
 * or an unparseable timestamp) is never past the deadline.
 */
export function isForcedUpdatePastDeadline(
  pendingForMs: number | null,
  deadlineMs: number = FORCED_UPDATE_HARD_DEADLINE_MS,
): boolean {
  return pendingForMs !== null && pendingForMs >= deadlineMs;
}

/** What the console should say about a pending Force Update request. */
export interface ForcedUpdatePendingSummary {
  /** Milliseconds since the operator clicked. */
  pendingForMs: number;
  /** Compact age for the chip label, e.g. `4m` / `1h 18m`. */
  age: string;
  /**
   * Past {@link FORCED_UPDATE_HARD_DEADLINE_MS} — the host should have stopped
   * deferring by now, so a chip still showing means something is wrong with the
   * host rather than merely busy.
   */
  overdue: boolean;
}

/**
 * Describe a pending Force Update for display. Null when nothing is pending, so
 * the caller renders no chip at all.
 *
 * `age` is deliberately coarse (whole minutes above a minute): the value of the
 * chip is "this has been sitting for 20 minutes, not 20 seconds", and a
 * server-rendered second count would be stale the moment it painted.
 */
export function describeForcedUpdatePending(opts: {
  requestedAt: string | null | undefined;
  nowMs: number;
  deadlineMs?: number;
}): ForcedUpdatePendingSummary | null {
  const pendingForMs = forcedUpdatePendingForMs(opts);
  if (pendingForMs === null) return null;
  const totalMinutes = Math.floor(pendingForMs / 60_000);
  const age =
    totalMinutes < 1
      ? '<1m'
      : totalMinutes < 60
        ? `${totalMinutes}m`
        : `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`;
  return {
    pendingForMs,
    age,
    overdue: isForcedUpdatePastDeadline(pendingForMs, opts.deadlineMs),
  };
}
